import React, { useState, useEffect } from 'react';
import { Table, Card, Space, Tag, Typography, Input, Button, message } from 'antd';
import { MailOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  border: 1px solid ${COLORS.border};
`;

const BirthdayEmailsLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [filters, setFilters] = useState({
    recipientEmail: '',
    dateRange: null as [Dayjs, Dayjs] | null,
  });

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    fetchEmails();
  }, [pagination.current, pagination.pageSize]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params: any = {
        type: 'BIRTHDAY',
        page: pagination.current,
        perPage: pagination.pageSize,
      };

      if (filters.recipientEmail) {
        params.recipientEmail = filters.recipientEmail;
      }

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_INTERNAL_EMAILS, { params });
      if (response.data?.status === 'success' && response.data?.data) {
        setEmails(response.data.data.emails || []);
        setTotal(response.data.data.pagination?.total || 0);
      }
    } catch (error: any) {
      message.error('Failed to fetch birthday emails');
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchEmails();
  };

  const handleReset = () => {
    setFilters({
      recipientEmail: '',
      dateRange: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => fetchEmails(), 100);
  };

  const columns = [
    {
      title: 'Recipient Email',
      dataIndex: 'recipientEmail',
      key: 'recipientEmail',
      render: (email: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{email}</span>
      ),
    },
    {
      title: 'Recipient Type',
      dataIndex: 'recipientUserType',
      key: 'recipientUserType',
      render: (type: string) => (
        <Tag color={type === 'Administrator' ? 'blue' : 'green'}>{type || '-'}</Tag>
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Sent',
      dataIndex: 'emailSent',
      key: 'emailSent',
      render: (sent: boolean) => (
        <Tag color={sent ? 'green' : 'red'}>{sent ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'emailSentAt',
      key: 'emailSentAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'Error',
      dataIndex: 'emailError',
      key: 'emailError',
      render: (error: string) => error ? (
        <Text type="danger" style={{ fontSize: '12px' }}>{error.substring(0, 50)}...</Text>
      ) : '-',
      ellipsis: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          <MailOutlined /> Birthday Emails Log
        </Title>
        <Button icon={<ReloadOutlined />} onClick={fetchEmails}>
          Refresh
        </Button>
      </HeaderContainer>

      <StyledCard>
        <FilterContainer>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Input
                placeholder="Filter by recipient email"
                value={filters.recipientEmail}
                onChange={(e) => setFilters({ ...filters, recipientEmail: e.target.value })}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
                onPressEnter={handleSearch}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
              <Button onClick={handleReset}>
                Reset
              </Button>
            </Space>
          </Space>
        </FilterContainer>

        <Table
          columns={columns}
          dataSource={emails}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} birthday emails`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </StyledCard>
    </PageContainer>
  );
};

export default BirthdayEmailsLog;

